import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import {
    calculatePercentage,
    formatDataGeneral,
    getAttributeValue,
    getFileLinkIfExist,
} from "utils/utils";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import {
    renderDpatEvidenceLink,
    scoreOnlyIfDocumentLinksComplete,
} from "utils/renderDpatEvidenceLink";
import { getDpatIncidentYears, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";

const AAP_DOCUMENT_TYPE = "Annual Action Plan Progress Report";
const AAP_DOCUMENT_TITLE = "Annual Action Plan Progress Report";
const PROGRESS_REPORT_TYPE = "Annual Progress Report";
const PROGRESS_REPORT_TITLE = "Annual Progress Report";
const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";
const AAP_PROGRAM = "ArLnAxhykoz";
const AAP_YEAR_ATTRIBUTE = "pWkYqcKukAY";
const PROJECT_PROGRAM = "g3wMUKEMmH3";

function getYearFromDate(value) {
    if (value == null || value === "" || value === "N/A") return null;

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.getFullYear();

    const match = String(value).match(/\b(19|20)\d{2}\b/);
    return match ? Number(match[0]) : null;
}

function startsWithinYear(item, selectedYear) {
    const year = Number(selectedYear);
    if (!Number.isFinite(year)) return false;

    return getYearFromDate(getAttributeValue("Start Date", item)) === year;
}

function fileUrl(eventId, dataElementUid) {
    return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function getDocumentHubLink(documents, documentType) {
    const documentData = documents?.data;
    const documentReports = documents?.reports;
    const matchedDocs = formatDataGeneral(
        documentData,
        "DPAT | M&E Tool Type",
        documentType,
    ) || [];

    if (!matchedDocs.length) return "";

    return getFileLinkIfExist(
        documentReports,
        DOCUMENT_HUB_FILE_ELEMENT,
        matchedDocs[0]?.trackedEntity,
    );
}

function buildAapDocumentLinks(documents) {
    const aapLink = getDocumentHubLink(documents, AAP_DOCUMENT_TYPE);
    const progressReportLink = getDocumentHubLink(documents, PROGRESS_REPORT_TYPE);

    return [
        {
            title: AAP_DOCUMENT_TITLE,
            link: aapLink
                ? fileUrl(aapLink, DOCUMENT_HUB_FILE_ELEMENT)
                : "Not Uploaded",
        },
        {
            title: PROGRESS_REPORT_TITLE,
            link: progressReportLink
                ? fileUrl(progressReportLink, DOCUMENT_HUB_FILE_ELEMENT)
                : "Not Uploaded",
        },
    ];
}

const AAPImplementation = forwardRef(({
    year,
    district,
    documents,
    data,
    hideComment,
    savedAssessmentData,
    assessmentStartLoading,
    isAssessmentStarted,
}, ref) => {

    const { Header, Content } = Layout;
    const { Title, Text } = Typography;
    const resolvedYear =
        year != null && typeof year === "object" && !Array.isArray(year)
            ? year.value ?? year.label
            : year;
    const analyticsYear = resolvedYear ?? new Date().getFullYear();
    const ouId =
        typeof district === "string"
            ? district
            : district?.value?.id ?? district?.id ?? "";
    const [aapImplementation, setAapImplementation] = useState([]);
    const [score, setScore] = useState(0);
    const [maxScore, setMaxScore] = useState(2);
    const [documentLinks, setDocumentLinks] = useState(data || []);
    const [percentages, setPercentages] = useState(0);

    const liveDocumentLinks = useMemo(
        () => buildAapDocumentLinks(documents),
        [documents],
    );

    const displayDocumentLinks = useMemo(() => {
        if (isAssessmentStarted && savedAssessmentData?.document?.length) {
            return savedAssessmentData.document;
        }
        if (documents) {
            return liveDocumentLinks;
        }
        return data || documentLinks;
    }, [
        isAssessmentStarted,
        savedAssessmentData?.document,
        documents,
        liveDocumentLinks,
        data,
        documentLinks,
    ]);

    useImperativeHandle(ref, () => ({
        getData: () => ({
            indicator: "PI1",
            area: "Annual Action Plan Implementation",
            maxScore,
            document: displayDocumentLinks,
            aapImplementation,
            score
        }),
    }));

    const DPAT_SLUG = "pi1.0-1.1";

    const aapImplementationColumns = [
        { title: `No. of activities in approved ${analyticsYear} Annual Action Plan`, dataIndex: "aapApproved", key: "aapApproved" },
        { title: "No. of activities in approved Annual Action Plan implemented", dataIndex: "aapImplented", key: "aapImplented" },
        { title: "% of implementation of activities in approved Annual Action Plan", dataIndex: "percentage", key: "percentage" }
    ];

    const aapDocumentColumns = [
        { title: "Title", dataIndex: "title", key: "title" },
        {
            title: "Document",
            dataIndex: "link",
            key: "link",
            render: (value) => renderDpatEvidenceLink(value, "View Document"),
        },
    ];

    const getIndicatorsData = useCallback(async () => {
        if (!ouId) return;

        setDocumentLinks(buildAapDocumentLinks(documents));

        try {
            const entityParams = {
                    orgUnit: ouId,
                    program: AAP_PROGRAM,
                    pageSize: 5000,
            };
            if (isDpatScoreRelaxationExempt()) {
                const years = getDpatIncidentYears(analyticsYear).join(";");
                entityParams.filter = `${AAP_YEAR_ATTRIBUTE}:IN:${years}`;
            } else {
                entityParams.filter = `${AAP_YEAR_ATTRIBUTE}:EQ:${analyticsYear}`;
            }
            const entitiesResp = await axios.get("/tracker/trackedEntities", {
                params: entityParams,
            });

            const approvedPlans = entitiesResp?.data?.instances || [];
            if (!approvedPlans.length) {
                setAapImplementation([]);
                setPercentages(0);
                setScore(0);
                return;
            }

            const projectEntitiesResp = await axios.get("/tracker/trackedEntities", {
                params: {
                    orgUnit: ouId,
                    program: PROJECT_PROGRAM,
                    pageSize: 5000,
                },
            });

            const approved = approvedPlans.length;
            const projectsAndProgrammes =
                projectEntitiesResp?.data?.instances || [];
            const implemented = projectsAndProgrammes.filter((item) =>
                startsWithinYear(item, analyticsYear),
            ).length;

            const percentage = approved > 0
                ? Number(calculatePercentage(implemented, approved).toFixed(2))
                : 0;

            setAapImplementation([
                {
                    key: "pi1-summary",
                    aapApproved: approved,
                    aapImplented: implemented,
                    percentage,
                },
            ]);
            setPercentages(percentage);
            const docLinks = buildAapDocumentLinks(documents);
            setDocumentLinks(docLinks);
            setScore(
                scoreOnlyIfDocumentLinksComplete(
                    docLinks,
                    percentage >= 90 ? 2 : 0,
                ),
            );
        } catch (err) {
            console.error("AAPImplementation fetch failed:", err);
            setAapImplementation([]);
            setPercentages(0);
            setScore(0);
        }
    }, [ouId, analyticsYear, documents]);

    useDpatIndicatorDataSource({
        isAssessmentStarted,
        savedAssessmentData,
        assessmentStartLoading,
        loadFromDhis2: getIndicatorsData,
        applySnapshot: (snap) => {
            if (snap.aapImplementation) setAapImplementation(snap.aapImplementation);
            if (snap.document) setDocumentLinks(snap.document);
            if (snap.score != null) setScore(snap.score);
            if (snap.maxScore != null) setMaxScore(snap.maxScore);
        },
        deps: [year, district, documents, getIndicatorsData],
    });

    return (
        <Comment
            data={aapImplementation}
            year={analyticsYear}
            districtId={ouId}
            tableCommentedId={`pi1.0-1.1-${analyticsYear}`}
            hideComment={hideComment}
        >
            {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
                    <DpatIndicatorCmsIntro
                        year={analyticsYear}
                        slug={DPAT_SLUG}
                        mainTitleStyle={{ marginTop: "20px" }}
                    />

                    <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                              <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.1 Actual Score:" score={score} />
                    </IndicatorScoreSection>
                    <Title level={4} style={{ marginTop: "20px" }}>I- Evidence of Annual Action Plan Implemented </Title>
                    <Table
                        columns={aapImplementationColumns}
                        dataSource={aapImplementation || []}
                        pagination={false}
                        bordered
                        rowKey="key"
                    />

                    <Title level={4} style={{ marginTop: "20px" }}>II- Evidence Attached Documents </Title>
                    <Table
                        columns={aapDocumentColumns}
                        dataSource={displayDocumentLinks}
                        pagination={false}
                        bordered
                        rowKey="title"
                    />

                    <Title level={5} style={{ marginTop: "20px" }}>Conclusion</Title>
                    <Content>
                        The district has implemented {percentages}% of Planned Projects and Programmes in the {analyticsYear} AAP
                    </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
            )}
        </Comment>
    );
})

export default AAPImplementation;